import { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import { useAction, useMutation } from 'convex/react'
import { Layers, PanelRightClose, PanelRightOpen, Users, Video } from 'lucide-react'
import { ExportButton } from '@/components/workflow/WorkflowDocument'
import { TabButton, PreviewPlayer, ClipListWithSeek } from '@/routes/video/[id]'
import { VideoSeekProvider } from '@/contexts/video-seek-context'
import { api } from '../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { Composer, WelcomeBlock, type AspectRatio } from '@/components/workflow/WelcomeComposer'
import { getVideoDurationFromFile, type ChatAttachment } from '@/hooks/useWorkflowChat'
import { AuthModal } from '@/components/auth/AuthModal'
import type { AuthMode } from '@/components/auth/AuthForm'
import { track } from '@/lib/track'
import { cn } from '@/lib/utils'
import { usePaywallVariant } from '@/platform/feature-flags/usePaywallVariant'
import { HardPaywallModal } from '@/components/billing/HardPaywallModal'
import { OG_IMAGE_URL, siteUrl } from '@/lib/config/site'

export default function HomePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const createWorkflow = useMutation(api.workflows.create)
  const sendMessageMutation = useMutation(api.workflows.sendMessage)
  const generateUploadUrl = useMutation(api.workflows.generateUploadUrl)
  const getStorageUrl = useMutation(api.workflows.getStorageUrl)
  const processDocument = useAction(api.documentProcessing.processDocument)

  // Composer text persists across the post-signin hard reload so the visitor's
  // typed prompt isn't lost when they sign up mid-flow.
  const [input, setInput] = useState(() => localStorage.getItem('footage_composer_draft') ?? '')
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('16:9')
  const [signupOpen, setSignupOpen] = useState(false)
  const [authMode, setAuthMode] = useState<AuthMode>('signup')
  const [submitting, setSubmitting] = useState(false)
  // Paywall pricing experiment: hardpaywall99 fresh signups must subscribe to
  // $99 Pro before creating a project. Resolves to control for everyone else.
  const { isHardPaywall } = usePaywallVariant()
  const [paywallOpen, setPaywallOpen] = useState(false)
  // The homepage doesn't have a real preview pane yet — toggling this only
  // flips the icon so the visual matches /video/:id's chat header.
  const [previewOpen, setPreviewOpen] = useState(false)
  // Decorative-only state for the preview pane's Clips/Characters/Timeline
  // tabs on the homepage — there's no workflow yet so the panes are empty,
  // but the toggle still functions visually.
  const [previewTab, setPreviewTab] = useState<'clips' | 'characters' | 'timeline'>('clips')

  useEffect(() => {
    track('page_view', { path: '/' })
  }, [])

  useEffect(() => {
    if (input) localStorage.setItem('footage_composer_draft', input)
    else localStorage.removeItem('footage_composer_draft')
  }, [input])

  const requireAuth = useCallback(() => {
    if (user && !user.isAnonymous) return false
    setAuthMode('signup')
    setSignupOpen(true)
    return true
  }, [user])

  const uploadFiles = useCallback(async (files: File[]): Promise<ChatAttachment[]> => {
    const attachments: ChatAttachment[] = []
    for (const file of files) {
      const uploadUrl = await generateUploadUrl()
      const res = await fetch(uploadUrl, { method: 'POST', body: file, headers: { 'Content-Type': file.type } })
      if (!res.ok) throw new Error(`Upload failed: ${file.name}`)
      const { storageId } = await res.json()
      const url = await getStorageUrl({ storageId })
      if (!url) continue

      if (file.type.startsWith('video/')) {
        const duration = await getVideoDurationFromFile(file)
        attachments.push({ type: 'video', url, storageId, name: file.name, mimeType: file.type, duration })
      } else if (file.type.startsWith('image/')) {
        attachments.push({ type: 'image', url, storageId, name: file.name })
      } else if (file.type.startsWith('audio/')) {
        const duration = await readAudioDuration(file).catch(() => 0)
        attachments.push({ type: 'audio', url, storageId, name: file.name, duration, mimeType: file.type })
      } else {
        let extractedText: string | undefined
        let pageCount: number | undefined
        try {
          const r = await processDocument({ storageId, filename: file.name, mimeType: file.type })
          extractedText = r.extractedText
          pageCount = r.pageCount
        } catch (e) {
          console.warn('Document parse failed:', e)
        }
        attachments.push({ type: 'document', url, storageId, name: file.name, mimeType: file.type, extractedText, pageCount })
      }
    }
    return attachments
  }, [generateUploadUrl, getStorageUrl, processDocument])

  const handleSend = useCallback(async (files?: File[]) => {
    const msg = input.trim()
    if (!msg && !(files && files.length)) return
    if (submitting) return

    // Anonymous flow is disabled for now — Send without a real account just
    // pops the sign-up modal. We'll bring real anon support back once it's
    // stable end-to-end.
    if (requireAuth()) return

    // Hard paywall (experiment): a hardpaywall99 fresh signup must subscribe to
    // $99 Pro before a workflow is created. Block here and open checkout; the
    // typed prompt is already persisted to localStorage, so after they subscribe
    // they hit Send again and proceed (now control-effective). Control and all
    // existing/subscribed users skip this entirely.
    if (isHardPaywall) {
      setPaywallOpen(true)
      return
    }

    setSubmitting(true)
    try {
      let attachments: ChatAttachment[] | undefined
      if (files && files.length) attachments = await uploadFiles(files)
      const result = await createWorkflow({ settings: { aspectRatio } })
      await sendMessageMutation({ workflowId: result.id, content: msg, attachments })
      setInput('')
      localStorage.removeItem('footage_composer_draft')
      navigate(`/video/${result.shortcode}`)
    } catch (err) {
      console.error('[home] send failed:', err)
      setSubmitting(false)
    }
  }, [input, submitting, requireAuth, isHardPaywall, uploadFiles, createWorkflow, sendMessageMutation, navigate, aspectRatio])

  return (
    <div className="h-full w-full flex flex-row bg-background text-zinc-900 relative">
      <Helmet>
        <title>Footage — AI Video Generator</title>
        <meta name="description" content="Footage is an AI video generator that writes creative briefs, generates clips, and refines every frame so you can focus on your creative vision." />
        <link rel="canonical" href={siteUrl()} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Footage — AI Video Generator" />
        <meta property="og:description" content="Footage is an AI video generator that writes creative briefs, generates clips, and refines every frame so you can focus on your creative vision." />
        <meta property="og:url" content={siteUrl()} />
        <meta property="og:image" content={OG_IMAGE_URL} />
      </Helmet>

      {/* Left section — chat / composer */}
      <section className="flex-1 min-w-0 relative flex flex-col">
        {/* Floating top-right preview pane toggle — matches /video/:id. */}
        <div className="absolute top-4 right-4 z-30 hidden md:block">
          <div className="relative group">
            <button
              type="button"
              onClick={() => setPreviewOpen((o) => !o)}
              className="p-1.5 rounded-md bg-white/80 backdrop-blur-sm border border-zinc-200/70 shadow-sm hover:bg-white text-zinc-600"
            >
              {previewOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
            </button>
            <div className="pointer-events-none absolute right-0 top-full mt-2 z-50 inline-flex items-center h-8 px-3 rounded-full bg-zinc-900 text-white text-[13px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
              {previewOpen ? 'Close preview' : 'Open preview'}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col justify-start pt-[18vh] md:pt-[32vh]">
          <div className="px-8">
            <div className="max-w-[720px] mx-auto pb-[30px]">
              <WelcomeBlock />
            </div>
          </div>
          <div className="px-8 shrink-0">
            <div className="max-w-[720px] mx-auto">
              <Composer
                value={input}
                onChange={setInput}
                onSend={handleSend}
                isStreaming={submitting}
                aspectRatio={aspectRatio}
                onAspectRatioChange={setAspectRatio}
                onAttachClick={requireAuth}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Right section — preview pane. Uses the exact same components as
          /video/:id so styling is identical: ExportButton, PreviewPlayer,
          TabButton, ClipListWithSeek. */}
      <VideoSeekProvider>
        <aside
          className={cn(
            'flex-col bg-background overflow-hidden hidden',
            'md:flex md:shrink-0 md:flex-none md:transition-[width,border-color] md:duration-200 md:ease-out',
            previewOpen
              ? 'md:w-[420px] [@media(min-width:1100px)]:md:w-[560px] md:border-l md:border-zinc-200/70'
              : 'md:w-0 md:border-l md:border-transparent',
          )}
          aria-hidden={!previewOpen}
        >
          <div className="w-full md:w-[420px] [@media(min-width:1100px)]:md:w-[560px] flex flex-col h-full">
            <div className="shrink-0 relative h-14 px-5 flex items-center justify-end border-b border-zinc-200/70">
              <h2 className="absolute left-1/2 -translate-x-1/2 text-[15px] font-medium text-zinc-900 tracking-tight">Video Preview</h2>
              <ExportButton
                workflowId={null}
                workflow={{}}
                clips={undefined}
              />
            </div>
            <div className="flex-1 min-h-0 flex flex-col px-5 pt-5 pb-6 gap-4">
              <div className="shrink-0">
                <PreviewPlayer
                  clips={[]}
                  selectedClipId={null}
                  onSelectClip={() => {}}
                  aspectRatio={aspectRatio}
                  workflow={null}
                  workflowId={null}
                />
              </div>

              <div className="shrink-0 flex items-center gap-1 p-1 rounded-xl bg-white border border-zinc-200/70 shadow-sm w-fit">
                <TabButton active={previewTab === 'clips'} onClick={() => setPreviewTab('clips')} icon={<Video className="h-3.5 w-3.5" />}>Clips</TabButton>
                <TabButton active={previewTab === 'characters'} onClick={() => setPreviewTab('characters')} icon={<Users className="h-3.5 w-3.5" />}>Characters</TabButton>
                <TabButton active={previewTab === 'timeline'} onClick={() => setPreviewTab('timeline')} icon={<Layers className="h-3.5 w-3.5" />}>Timeline</TabButton>
              </div>

              {previewTab === 'clips' ? (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <ClipListWithSeek
                    clips={[]}
                    characters={undefined}
                    timelineMode={undefined}
                    selectedId={null}
                    onSelect={() => {}}
                  />
                </div>
              ) : previewTab === 'characters' ? (
                <div className="rounded-xl border border-dashed border-zinc-200 p-8 text-center text-[12px] text-zinc-400">
                  No characters yet. Ask the agent to start.
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-zinc-200 p-8 text-center text-[12px] text-zinc-400">
                  Timeline will appear once you have clips.
                </div>
              )}
            </div>
          </div>
        </aside>
      </VideoSeekProvider>

      <AuthModal
        open={signupOpen}
        mode={authMode}
        onModeChange={setAuthMode}
        onOpenChange={setSignupOpen}
      />

      <HardPaywallModal open={paywallOpen} onOpenChange={setPaywallOpen} />
    </div>
  )
}

function readAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const audio = new Audio()
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(audio.src)
      resolve(audio.duration)
    }
    audio.onerror = () => {
      URL.revokeObjectURL(audio.src)
      reject(new Error('Failed to read audio metadata'))
    }
    audio.src = URL.createObjectURL(file)
  })
}
